import OmniSearch from './OmniSearch';
import { Grid, ExternalLink, Bell, Search, Settings, Sun, Moon, CheckCircle, AlertCircle, Info, X, LogOut, User } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL as CONFIG_API_URL } from '../config';

const API_URL = CONFIG_API_URL || 'https://www.sapientink.com:3002/api';

const formatTaskNo = (taskNo) => {
  if (!taskNo || taskNo.length <= 30) return taskNo;
  return taskNo.slice(0, 11) + '...' + taskNo.slice(-4);
};

export default function Header() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return true; // Default to dark mode
  });
  const location = useLocation();
  const currentSystem = location.pathname.startsWith('/seo') ? 'seo' : 'geo';

  const [brands, setBrands] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(true);

  const [selectedBrand, setSelectedBrand] = useState(localStorage.getItem('globalBrand') || '');
  const [selectedProject, setSelectedProject] = useState(localStorage.getItem('globalProject') || '');
  
  // Fetch Mode State
  const [fetchMode, setFetchMode] = useState(localStorage.getItem('globalFetchMode') || 'hybrid');
  
  // Custom Project Dropdown State
  const [isProjectOpen, setIsProjectOpen] = useState(false);
  const projectSelectRef = useRef(null);
  
  // Notification States
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showAllHistoryModal, setShowAllHistoryModal] = useState(false);

  const notifRef = useRef(null);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const appSwitcherRef = useRef(null);
  const settingsRef = useRef(null);
  const navigate = useNavigate();
  const authContext = useAuth();
  const user = authContext?.user;
  const role = user?.role || localStorage.getItem('role');

  // Active Users Tracker (Admin Only)
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [activeUsers, setActiveUsers] = useState([]);
  const [showActiveUsers, setShowActiveUsers] = useState(false);
  const activeUsersRef = useRef(null);
  
  useEffect(() => {
    function handleClickOutside(event) {
      if (activeUsersRef.current && !activeUsersRef.current.contains(event.target)) {
        setShowActiveUsers(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      const fetchActiveUsers = async () => {
        try {
          const res = await fetch(`${API_URL}/admin/active-users`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          const data = await res.json();
          if (data.success) {
            setActiveUsersCount(data.count);
            setActiveUsers(data.users);
          }
        } catch (e) {}
      };
      fetchActiveUsers();
      const interval = setInterval(fetchActiveUsers, 30000); // 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  // Helper to update and dispatch
  const updateGlobalProject = (val) => {
    setSelectedProject(val);
    localStorage.setItem('globalProject', val);
    window.dispatchEvent(new Event('globalFilterChange'));
  };

  const updateGlobalBrand = (val) => {
    setSelectedBrand(val);
    localStorage.setItem('globalBrand', val);
    window.dispatchEvent(new Event('globalFilterChange'));
  };

  const updateGlobalFetchMode = (val) => {
    setFetchMode(val);
    localStorage.setItem('globalFetchMode', val);
    window.dispatchEvent(new Event('globalFilterChange'));
  };


  useEffect(() => {
    const handleForceSync = () => {
      setSelectedBrand(localStorage.getItem('globalBrand') || 'all');
      setSelectedProject(localStorage.getItem('globalProject') || 'all');
    };
    window.addEventListener('forceHeaderSync', handleForceSync);
    return () => window.removeEventListener('forceHeaderSync', handleForceSync);
  }, []);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (projectSelectRef.current && !projectSelectRef.current.contains(event.target)) {
        setIsProjectOpen(false);
      }
      if (appSwitcherRef.current && !appSwitcherRef.current.contains(event.target)) {
        setShowAppSwitcher(false);
      }
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/announcements?system=${currentSystem}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Load brands and tasks on mount/system change
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingBrands(true);
      try {
        const token = localStorage.getItem('token') || '';
        const headers = { 'Authorization': `Bearer ${token}` };
        
        const [brandRes, taskRes] = await Promise.all([
          fetch(`${API_URL}/brand-projects?system=${currentSystem}`, { headers }),
          fetch(`${API_URL}/geo-tasks?system=${currentSystem}`, { headers })
        ]);

        const brandData = await brandRes.json();
        const taskData = await taskRes.json();

        let initialBrand = selectedBrand;
        if (brandData.success) {
          setBrands(brandData.data);
          
          // Verify if the current selectedBrand exists in the new system's brands
          const exists = brandData.data.some(b => b.id.toString() === selectedBrand);
          if (!selectedBrand || (!exists && selectedBrand !== 'all')) {
            initialBrand = 'all';
            updateGlobalBrand(initialBrand);
          }
        }
        
        if (taskData.success) {
          setTasks(taskData.data);
          if (initialBrand && initialBrand !== 'all') {
            const filtered = taskData.data.filter(t => t.brandProjectId.toString() === initialBrand && t.type !== 'scheduled');
            const projectExists = filtered.some(t => t.id.toString() === selectedProject);
            if (!projectExists && filtered.length > 0) {
               updateGlobalProject(filtered[0].id.toString());
            }
          } else {
            const filteredAll = taskData.data.filter(t => t.type !== 'scheduled');
            const projectExistsAll = filteredAll.some(t => t.id.toString() === selectedProject);
            if (!projectExistsAll && filteredAll.length > 0) {
               updateGlobalProject('all');
            }
          }
        }
      } catch (err) {
        console.error('Error fetching global header data:', err);
      } finally {
        setIsLoadingBrands(false);
      }
    };
    fetchData();
  }, [currentSystem]); // Dependency array now includes currentSystem

  // Notifications fetch effect decoupled from brands
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [currentSystem]);

  // Filter tasks by selected brand. Ignore scheduled tasks for the project dropdown.
  const filteredTasks = tasks.filter(t => {
    if (t.type === 'scheduled') return false;
    if (selectedBrand !== 'all' && t.brandProjectId.toString() !== selectedBrand) return false;
    if (fetchMode !== 'hybrid' && t.fetchMode !== fetchMode) return false;
    return true;
  });

  // If the selected project is not in the filtered tasks, reset it
  useEffect(() => {
    if (filteredTasks.length > 0) {
      const exists = filteredTasks.some(t => t.id.toString() === selectedProject);
      if (!exists && selectedProject !== 'all') {
        updateGlobalProject('all');
      }
    } else if (selectedProject) {
      updateGlobalProject('');
    }
  }, [selectedBrand, filteredTasks]);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  
  const handleSSOJump = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
         window.location.href = 'https://www.sapientink.com';
         return;
      }
      const meRes = await fetch(`${API_URL}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` }});
      const meData = await meRes.json();
      const user = meData.user;
      if (!user) {
         window.location.href = 'https://www.sapientink.com';
         return;
      }
      
      const ticketRes = await fetch(`${API_URL}/sso/generate-ticket`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
         body: JSON.stringify({ email: user.email, name: user.name, registeredFrom: user.registeredFrom || 'nexgeo' })
      });
      const ticketData = await ticketRes.json();
      if (ticketData.ticket) {
         window.open(`https://www.sapientink.com/sso-login?ticket=${ticketData.ticket}`, '_blank');
      } else {
         window.open('https://www.sapientink.com', '_blank');
      }
    } catch(err) {
      console.error('SSO Jump Error:', err);
      window.open('https://www.sapientink.com', '_blank');
    }
  };

  const handleBrandChange = (e) => {
    updateGlobalBrand(e.target.value);
  };

  const handleProjectChange = (e) => {
    updateGlobalProject(e.target.value);
  };

  const markAsRead = async (id, type) => {
    if (type !== 'alert') return; // System announcements don't have read status for now
    try {
      const token = localStorage.getItem('token') || '';
      await fetch(`${API_URL}/announcements/${id}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      await fetch(`${API_URL}/announcements/read-all?system=${currentSystem}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const hideFiltersPaths = ['/tasks', '/settings', '/system', '/seed-tasks', '/accounts'];
  const shouldHideFilters = hideFiltersPaths.some(path => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const unreadCount = notifications.filter(n => n.type === 'alert' && !n.isRead).length;

  return (
    <header className="h-16 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between px-6 shadow-sm transition-colors duration-200 z-50 relative">
      <div className="flex-1 flex items-center space-x-3 xl:space-x-4">
        {!shouldHideFilters && (
          <>
            <div className="flex-shrink-0 relative group">
              <select 
                value={selectedBrand}
                onChange={handleBrandChange}
                className="appearance-none bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-slate-200 text-sm font-medium rounded-lg focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 block w-40 pl-3 pr-8 py-2 transition-colors outline-none cursor-pointer"
              >
                {isLoadingBrands ? (
                  <option value="">-- 加载中 --</option>
                ) : brands.length === 0 ? (
                  <option value="">-- 暂无关联品牌 --</option>
                ) : (
                  <>
                    <option value="all">🌍 全部品牌大盘</option>
                    {brands.map(b => (
                      <option key={b.id} value={b.id}>👑 品牌：{b.name}{b.subBrandName ? b.subBrandName : ''}</option>
                    ))}
                  </>
                )}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 dark:text-slate-400">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>


            {/* Fetch Mode Segmented Control Switcher */}
            {currentSystem !== 'seo' && (
            <div className="flex-shrink-0 flex bg-gray-100 dark:bg-slate-800 p-1 rounded-lg border border-gray-200 dark:border-slate-700 shadow-inner">
              <button 
                onClick={() => updateGlobalFetchMode('hybrid')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${fetchMode === 'hybrid' ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'}`}
                title="混合模式：同时包含API和RPA数据"
              >
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-sm"></div> 混合
              </button>
              <button 
                onClick={() => updateGlobalFetchMode('api')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${fetchMode === 'api' ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'}`}
                title="API实时模式：只看大模型直连实时数据"
              >
                <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm"></div> API
              </button>
              <button 
                onClick={() => updateGlobalFetchMode('rpa')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${fetchMode === 'rpa' ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'}`}
                title="RPA深度模式：只看RPA无头浏览器抓取的深度数据"
              >
                <div className="w-2.5 h-2.5 rounded-full bg-purple-500 shadow-sm"></div> RPA
              </button>
            </div>
            )}

            
            <div className="flex-shrink-0 relative group" ref={projectSelectRef}>
              <div 
                onClick={() => setIsProjectOpen(!isProjectOpen)}
                className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-slate-200 text-sm font-medium rounded-lg flex items-center justify-between w-72 xl:w-[22rem] pl-3 pr-2 py-2 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2 overflow-hidden whitespace-nowrap text-ellipsis flex-1">
                  {isLoadingBrands ? (
                    <span className="text-gray-500">-- 加载中 --</span>
                  ) : filteredTasks.length === 0 ? (
                    <span className="text-gray-500">-- 暂无抓取任务 --</span>
                  ) : selectedProject === 'all' || !selectedProject ? (
                    <span>🌍 整体大盘 (所有任务)</span>
                  ) : (() => {
                    const t = filteredTasks.find(t => t.id.toString() === selectedProject);
                    if (!t) return <span>🌍 整体大盘 (所有任务)</span>;
                    return (
                      <>
                        <div className="flex items-center gap-2 overflow-hidden">
                        {t.fetchMode === 'rpa' 
                          ? <div className="w-2.5 h-2.5 rounded-full bg-purple-500 shadow-sm flex-shrink-0"></div>
                          : <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm flex-shrink-0"></div>}
                        <span className="truncate" title={t.taskNo}>{t.type === 'scheduled' ? '⏱️ ' : ''}{formatTaskNo(t.taskNo)}</span>
                      </div>
                      <span className="text-gray-400 text-xs whitespace-nowrap flex-shrink-0">({formatDate(t.createdAt)})</span>
                      </>
                    );
                  })()}
                </div>
                <div className="flex items-center px-1 text-gray-500 dark:text-slate-400">
                  <svg className={`fill-current h-4 w-4 flex-shrink-0 transition-transform ${isProjectOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
              
              {isProjectOpen && (
                <div className="absolute z-50 top-full mt-1 left-0 w-full max-h-60 overflow-y-auto bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg py-1">
                  {filteredTasks.length > 0 && (
                    <div 
                      className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-900 dark:text-slate-200 flex items-center gap-2"
                      onClick={() => { handleProjectChange({ target: { value: 'all' } }); setIsProjectOpen(false); }}
                    >
                      🌍 整体大盘 (所有任务)
                    </div>
                  )}
                  {filteredTasks.length === 0 && !isLoadingBrands && (
                     <div className="px-3 py-2 text-sm text-gray-500 dark:text-slate-400">-- 暂无抓取任务 --</div>
                  )}
                  {filteredTasks.map(t => (
                    <div 
                      key={t.id}
                      title={t.taskNo}
                      className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-900 dark:text-slate-200 flex items-center justify-between gap-2"
                      onClick={() => { handleProjectChange({ target: { value: t.id.toString() } }); setIsProjectOpen(false); }}
                    >
                      <div className="flex items-center gap-2 overflow-hidden">
                        {t.fetchMode === 'rpa' 
                          ? <div className="w-2.5 h-2.5 rounded-full bg-purple-500 shadow-sm flex-shrink-0"></div>
                          : <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm flex-shrink-0"></div>}
                        <span className="truncate" title={t.taskNo}>{t.type === 'scheduled' ? '⏱️ ' : ''}{formatTaskNo(t.taskNo)}</span>
                      </div>
                      <span className="text-gray-400 text-xs whitespace-nowrap flex-shrink-0">({formatDate(t.createdAt)})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <OmniSearch projectId={selectedProject} />
          </>
        )}
      </div>
      <div className="flex items-center space-x-4">
        <button 
          onClick={() => setIsDark(!isDark)}
          className="text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition"
          title={isDark ? "切换到浅色模式" : "切换到深色模式"}
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        <div className="relative" ref={notifRef}>
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition relative"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-100 dark:border-slate-700 overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 dark:text-slate-100">消息中心</h3>
                {unreadCount > 0 && (
                  <button onClick={markAllAsRead} className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300">
                    全部标为已读
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 dark:text-slate-400 text-sm">
                    暂无新消息
                  </div>
                ) : (
                  <div className="divide-y divide-gray-50 dark:divide-slate-700">
                    {notifications.map(n => (
                      <div 
                        key={n.id} 
                        onClick={() => { markAsRead(n.id, n.type); setSelectedNotification(n); setShowNotifications(false); }}
                        className={`p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors ${n.type === 'alert' && !n.isRead ? 'bg-indigo-50/30 dark:bg-indigo-900/10' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          {n.type === 'alert' ? (
                            <AlertCircle className={`w-5 h-5 mt-0.5 ${!n.isRead ? 'text-red-500' : 'text-gray-400 dark:text-slate-500'}`} />
                          ) : (
                            <Info className="w-5 h-5 mt-0.5 text-blue-500" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium text-gray-900 dark:text-slate-100 ${n.type === 'alert' && !n.isRead ? 'font-bold' : ''}`}>
                              {n.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 whitespace-pre-wrap break-words line-clamp-3">
                              {n.content}
                            </p>
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-2">
                              {formatDate(n.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-4 py-2 border-t border-gray-100 dark:border-slate-700 text-center bg-gray-50 dark:bg-slate-800/50">
                <button 
                  onClick={() => { setShowNotifications(false); setShowAllHistoryModal(true); }}
                  className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                >
                  查看全部历史消息
                </button>
              </div>
            </div>
          )}
        </div>

        
        
        {/* App Switcher */}
        {user?.allowSso !== false && (
          <div className="relative" ref={appSwitcherRef}>
            <button 
              onClick={() => setShowAppSwitcher(!showAppSwitcher)}
              className="text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition"
            >
              <Grid className="w-5 h-5" />
            </button>
            
            {showAppSwitcher && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-100 dark:border-slate-700 overflow-hidden z-50 p-3">
                <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-2 px-1">一站式 AI 矩阵</div>
                <div className="grid grid-cols-2 gap-2">

                  <button className="flex flex-col items-center justify-center p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800/50 hover:shadow-md transition">
                    <div className="w-8 h-8 flex items-center justify-center mb-2 relative">
                      <img src="/sixu_logo_transparent.png" alt="Sapient Rank" className="w-8 h-8 object-contain" />
                    </div>
                    <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">思序 Sapient Rank (当前)</span>
                  </button>

                  <button 
                    onClick={handleSSOJump}
                    className="flex flex-col items-center justify-center p-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50 hover:shadow-md transition group"
                  >
                    <div className="w-8 h-8 flex items-center justify-center mb-2 relative">
                      <img src="/zhimo_logo_transparent.png" alt="Sapient Ink" className="w-8 h-8 object-contain" />
                      <ExternalLink className="w-3 h-3 absolute -top-1 -right-1 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <span className="text-xs font-medium text-gray-800 dark:text-slate-200">智墨 Sapient Ink</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        
        {/* Active Users Radar (Admin) */}
        {role === 'ADMIN' && (
          <div className="relative mr-2" ref={activeUsersRef}>
            <button
              onClick={() => setShowActiveUsers(!showActiveUsers)}
              className="flex items-center px-3 py-1.5 rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors border border-green-200 dark:border-green-800/50"
              title="当前在线活跃用户 (近15分钟)"
            >
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse mr-2"></div>
              <span className="text-xs font-medium">在线: {activeUsersCount}</span>
            </button>

            {showActiveUsers && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-100 dark:border-slate-700 z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                    在线用户雷达
                  </h3>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {activeUsers.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      当前除您之外无其他活跃用户
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-slate-700">
                      {activeUsers.map(u => (
                        <div key={u.id} className="p-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-slate-200 truncate">{u.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 rounded">
                              {u.role === 'ADMIN' ? '超管' : '客户'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{u.email}</div>
                          <div className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                            最后活跃: {new Date(u.lastActive).toLocaleTimeString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="relative" ref={settingsRef}>
          <button 
            onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
            className="text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition"
          >
            <Settings className="w-5 h-5" />
          </button>
          
          {showSettingsDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-100 dark:border-slate-700 overflow-hidden z-50 py-1">
              <button 
                onClick={() => { setShowSettingsDropdown(false); navigate('/settings/profile'); }}
                className="w-full px-4 py-2 text-left flex items-center gap-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
              >
                <User className="w-4 h-4 text-gray-400" />
                个人设置
              </button>
              {role === 'ADMIN' && (
                <button 
                  onClick={() => { setShowSettingsDropdown(false); navigate('/system/users'); }}
                  className="w-full px-4 py-2 text-left flex items-center gap-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <Settings className="w-4 h-4 text-gray-400" />
                  系统管理
                </button>
              )}
              <div className="h-px bg-gray-100 dark:bg-slate-700 my-1"></div>
              <button 
                onClick={() => { 
                  setShowSettingsDropdown(false); 
                  localStorage.clear(); 
                  window.location.href = '/login'; 
                }}
                className="w-full px-4 py-2 text-left flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                退出登录
              </button>
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-gray-200 dark:bg-slate-700 mx-2"></div>
        <button onClick={() => navigate('/guide')} title="使用帮助" className="text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition relative">
          <Info className="w-5 h-5" />
        </button>
      </div>


      {/* All History Modal */}
      {showAllHistoryModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                <Bell className="w-5 h-5 text-indigo-500" />
                消息中心 (历史记录)
              </h2>
              <button 
                onClick={() => setShowAllHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-slate-900">
              {notifications.length === 0 ? (
                <div className="text-center text-gray-500 py-10">暂无历史消息</div>
              ) : (
                <div className="space-y-4">
                  {notifications.map(n => (
                    <div 
                      key={n.id} 
                      onClick={() => { markAsRead(n.id, n.type); setSelectedNotification(n); }}
                      className={`p-4 bg-white dark:bg-slate-800 rounded-xl border ${n.type === 'alert' && !n.isRead ? 'border-indigo-200 dark:border-indigo-800/50 shadow-sm' : 'border-gray-100 dark:border-slate-700'} cursor-pointer hover:shadow-md transition-all`}
                    >
                      <div className="flex items-start gap-3">
                        {n.type === 'alert' ? (
                          <AlertCircle className={`w-5 h-5 mt-0.5 ${!n.isRead ? 'text-red-500' : 'text-gray-400 dark:text-slate-500'}`} />
                        ) : (
                          <Info className="w-5 h-5 mt-0.5 text-blue-500" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <h4 className={`text-sm font-medium ${!n.isRead && n.type === 'alert' ? 'text-gray-900 dark:text-white font-bold' : 'text-gray-700 dark:text-slate-200'}`}>
                              {n.title}
                            </h4>
                            <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                              {new Date(n.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-slate-400 mt-2 line-clamp-2">
                            {n.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
              <div className="flex items-center gap-3">
                {selectedNotification.type === 'alert' ? (
                  <AlertCircle className="w-6 h-6 text-red-500" />
                ) : (
                  <Info className="w-6 h-6 text-blue-500" />
                )}
                <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  {selectedNotification.title}
                </h2>
              </div>
              <button 
                onClick={() => setSelectedNotification(null)}
                className="text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Body */}
            <div className="px-6 py-6 overflow-y-auto">
              <div className="text-sm text-gray-500 dark:text-slate-400 mb-6 flex items-center gap-2">
                <span>消息类型: {selectedNotification.type === 'alert' ? '系统预警' : '系统公告'}</span>
                <span>•</span>
                <span>发送时间: {new Date(selectedNotification.createdAt).toLocaleString()}</span>
              </div>
              <div className="prose prose-sm max-w-none dark:prose-invert text-gray-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                {selectedNotification.content}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 flex justify-end">
              <button 
                onClick={() => setSelectedNotification(null)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

